import { useState } from 'react';
import { supabase } from '@/lib/supabase';
import type { ProductType } from '../types';

interface ProductSuggestionInput {
  brand: string;
  product_name: string;
  product_type: ProductType;
  barcode?: string;
  similar_found: boolean;
  notes?: string;
}

interface IngredientReportInput {
  ingredient_name: string;
  product_type: ProductType;
  found_in_product?: string;
  notes?: string;
}

export function useSuggestions() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submitProductSuggestion = async (input: ProductSuggestionInput) => {
    setIsSubmitting(true);
    setError(null);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('Du musst angemeldet sein, um einen Vorschlag zu senden.');
      }

      const { error: insertError } = await supabase
        .from('product_suggestions')
        .insert({
          user_id: user.id,
          brand: input.brand,
          product_name: input.product_name,
          product_type: input.product_type,
          barcode: input.barcode || null,
          similar_found: input.similar_found,
          notes: input.notes || null,
        });

      if (insertError) {
        throw insertError;
      }

      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Fehler beim Senden des Vorschlags';
      setError(message);
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  const submitIngredientReport = async (input: IngredientReportInput) => {
    setIsSubmitting(true);
    setError(null);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('Du musst angemeldet sein, um eine Meldung zu senden.');
      }

      const { error: insertError } = await supabase
        .from('ingredient_reports')
        .insert({
          user_id: user.id,
          ingredient_name: input.ingredient_name,
          product_type: input.product_type,
          found_in_product: input.found_in_product || null,
          notes: input.notes || null,
        });

      if (insertError) {
        throw insertError;
      }

      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Fehler beim Senden der Meldung';
      setError(message);
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    submitProductSuggestion,
    submitIngredientReport,
    isSubmitting,
    error,
    clearError: () => setError(null),
  };
}
